import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { CircularProgress, IconButton } from '@mui/material'
import CustomInput from '../../components/TextInput'
import CustomButton from '../../components/Button'
import { useSnackbar } from 'notistack'
import { Tooltip } from 'react-tooltip'
import { PasswordTooltip } from '../../components/Tooltip'
import { AppConfig } from '../../models/shared'
import UtilitiesService from '../../services/UtilitiesService'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CustomIconButton from '../../components/IconButton'
import DeleteIcon from '@mui/icons-material/Delete'
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'

interface Props {
    handleClose: () => void
}
const ConfigurationForm = ({ handleClose }: Props) => {
    const [config, setConfig] = useState<AppConfig>()
    const [pending, setPending] = useState<boolean>(false)
    const [errors, setErrors] = useState<string[]>([])
    const { enqueueSnackbar } = useSnackbar()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [inputKey, setInputKey] = useState(0)

    useEffect(() => {
        new UtilitiesService().getAppConfig().then((response) => {
            if (response.status !== 200 || !response.data)
                setConfig({ university: '', careers: [''], logo: '', email: '' })
            else setConfig(response.data)
        })
    }, [])

    const validateInfo = () => {
        const newErrors: string[] = []
        config!.careers.forEach((career, index) => {
            if (config!.careers.some((x, xIndex) => index !== xIndex && career === x))
                newErrors[index] = 'Carrera repetida'
            else newErrors[index] = ''
        })

        setErrors(newErrors)
        return Object.values(newErrors).every((x) => x === '')
    }

    const handleSubmit = async () => {
        if (!validateInfo()) return

        setPending(true)
        const response = await new UtilitiesService().createNewConfig(config!)
        if (response.status !== 200) {
            enqueueSnackbar('Error en la solicitud.', {
                variant: 'error',
                autoHideDuration: 6000,
            })
        } else {
            enqueueSnackbar('Configuración actualizada correctamente.', {
                variant: 'success',
                autoHideDuration: 6000,
            })
        }
        setPending(false)
        handleClose()
    }

    const handleCareerChange = (index: number, value: string) => {
        const careers = config!.careers
        careers.splice(index, 1, value)
        setConfig((prev) => ({ ...prev!, careers: careers }))
    }

    const handleDeleteCareer = (index: number) => {
        let careers = config!.careers
        if (careers.length === 1) careers = ['']
        else {
            careers.splice(index, 1)
            const newErrors = errors
            newErrors.splice(index, 1)
            setErrors(newErrors)
        }
        setConfig((prev) => ({ ...prev!, careers: careers }))
    }

    const handleLogoUpload = (files?: FileList) => {
        if (files && files[0]) {
            const file = files[0]
            const reader = new FileReader()
            reader.onloadend = () => {
                const base64String = reader.result as string
                setConfig((prev) => ({
                    ...prev!,
                    logo: base64String,
                }))
            }
            reader.readAsDataURL(file)
        }
    }

    return (
        <>
            {config ? (
                <div className="w-[65vw] md:w-[50vw] max-h-[50vh] md:max-h-[60vh] flex flex-col gap-2 md:gap-4 text-primary md:px-3">
                    <span className="text-primary text-base md:text-xl font-semibold">
                        Configurar universidad
                    </span>
                    <CustomInput
                        type="text"
                        label="Nombre de la universidad"
                        placeholder="Ingresá el nombre de la universidad"
                        name="university"
                        value={config.university}
                        onChange={(e) =>
                            setConfig((prev) => ({ ...prev!, university: e.target.value }))
                        }
                    />
                    <CustomInput
                        type="text"
                        label="Email para ofertas laborales"
                        placeholder="Ingresá un email para enviar ofertas laborales"
                        name="email"
                        value={config.email}
                        onChange={(e) => setConfig((prev) => ({ ...prev!, email: e.target.value }))}
                    />
                    <div className="flex flex-col gap-2 md:gap-4">
                        <span className="text-sm md:text-base">Carreras:</span>
                        <div className="flex flex-col gap-2 md:gap-3">
                            {config.careers.map((carreer, index) => (
                                <div
                                    key={`career` + index}
                                    className="flex gap-1 md:gap-2"
                                >
                                    <CustomInput
                                        type="text"
                                        label="Nombre de la carrera"
                                        placeholder="Ingresá el nombre de la carrera"
                                        value={carreer}
                                        onChange={(e) => handleCareerChange(index, e.target.value)}
                                        className="w-full"
                                        errorText={errors[index]}
                                    />
                                    <CustomIconButton
                                        icon={<DeleteIcon />}
                                        className="px-2 md:px-4"
                                        onClick={() => handleDeleteCareer(index)}
                                    />
                                </div>
                            ))}
                        </div>
                        <CustomButton
                            secondary
                            startIcon={<AddCircleOutlineIcon />}
                            className="w-fit"
                            onClick={() =>
                                setConfig((prev) => ({ ...prev!, careers: [...prev!.careers, ''] }))
                            }
                        >
                            <span className="text-sm md:text-base">Agregar carrera</span>
                        </CustomButton>
                    </div>
                    <div className="flex flex-col gap-2 md:gap-4">
                        <span className="text-sm md:text-base">Logo:</span>
                        <CustomInput
                            key={inputKey}
                            id="image"
                            name="image"
                            type="file"
                            ref={fileInputRef}
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                handleLogoUpload(e.target.files as FileList)
                            }
                            className="hidden"
                            inputProps={{ accept: 'image/*' }}
                        />
                        {config.logo === '' ? (
                            <label htmlFor="image">
                                <CustomButton
                                    secondary
                                    component="span"
                                    startIcon={<AddPhotoAlternateIcon />}
                                >
                                    <span className="text-sm md:text-base">Subir logo</span>
                                </CustomButton>
                            </label>
                        ) : (
                            <div className="relative max-w-full">
                                <div className="relative inline-block max-w-full ">
                                    <img
                                        alt="logo"
                                        src={config.logo}
                                        className="max-w-full max-h-[100px] md:max-h-[200px]"
                                    />
                                    <IconButton
                                        onClick={() => {
                                            setConfig((prev) => ({ ...prev!, logo: '' }))
                                            setInputKey((prevKey) => prevKey + 1)
                                        }}
                                        className="absolute top-2 right-2 text-white bg-black p-0 rounded-full"
                                    >
                                        <HighlightOffIcon />
                                    </IconButton>
                                </div>
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <CustomButton
                            className="h-[36px]"
                            onClick={handleClose}
                            text="Cerrar"
                            secondary
                        />
                        <CustomButton
                            className="h-[36px]"
                            onClick={handleSubmit}
                            disabled={
                                pending ||
                                config.university.trim() === '' ||
                                config.careers.some((career) => career.trim() === '')
                            }
                        >
                            {!pending ? (
                                'Guardar'
                            ) : (
                                <CircularProgress
                                    className="text-primary"
                                    size={24}
                                />
                            )}
                        </CustomButton>
                    </div>
                </div>
            ) : (
                <div className="flex w-full justify-center">
                    <CircularProgress
                        className="m-2 md:m-5 text-primary"
                        size={64}
                    />
                </div>
            )}
        </>
    )
}

export default ConfigurationForm
